import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  mockPatient,
  clinicRecipient,
  processedItems,
  financialROI,
  globalComplianceText,
} from "@/mocks/demo-flow";

export default function InboundAlertPage() {
  const navigate = useNavigate();
  const [proceeding, setProceeding] = useState(false);

  const handleProceed = () => {
    setProceeding(true);
    setTimeout(() => {
      navigate("/clinic-signup");
    }, 700);
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeNow = new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-100 text-[10px] font-semibold text-foreground-700">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-lock-line text-[9px]"></i>
            </span>
            Secure Clinical Backend
          </span>
          <Link
            to="/demo-transition"
            className="text-[11px] md:text-xs font-semibold text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            ← Back
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-6 py-8 md:py-12 space-y-6 relative">
        {/* Subtle warm background texture */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-0 w-[400px] h-[400px] rounded-full bg-accent-100/20 blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full bg-primary-100/15 blur-[100px]" />
        </div>

        {/* ===== EMAIL NOTIFICATION CARD ===== */}
        <div className="relative z-10 bg-white rounded-2xl border border-background-200 overflow-hidden">
          {/* ---- Email Metadata Header ---- */}
          <div className="border-b border-background-100 bg-background-50/50 px-6 md:px-8 py-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-primary-600">QP</span>
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground-900">
                    Quick Prosthetics Clinical Network
                  </p>
                  <p className="text-[11px] text-foreground-400">
                    notifications@quickprosthetics.com
                  </p>
                </div>
              </div>
              <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                {today} at {timeNow}
              </span>
            </div>

            {/* To field */}
            <div className="flex items-baseline gap-2 pt-3 border-t border-background-100">
              <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold w-14 flex-shrink-0">
                To:
              </span>
              <span className="text-xs font-semibold text-foreground-700">
                {clinicRecipient.name}
              </span>
            </div>

            {/* Subject */}
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold w-14 flex-shrink-0">
                Subject:
              </span>
              <span className="text-sm font-bold text-foreground-950">
                New Patient — Ready to Schedule Consultation
              </span>
            </div>
          </div>

          {/* ---- Email Body ---- */}
          <div className="px-6 md:px-8 py-6 md:py-8 space-y-8">
            {/* Greeting */}
            <div className="space-y-4">
              <p className="text-sm text-foreground-700 leading-relaxed">
                Dear {clinicRecipient.leadProsthetist} and the{" "}
                {clinicRecipient.name} Team,
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                A patient — whose identifying information has been anonymized
                until your clinic authorizes the BAA and data lease — has
                selected{" "}
                <strong className="text-foreground-900">
                  {clinicRecipient.name}
                </strong>{" "}
                as their preferred provider and is waiting for you to contact
                them to schedule an initial consultation.
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                Quick Prosthetics has processed this patient&apos;s complete
                intake package in advance of your consultation. The file
                includes the physician medical evaluation, 3D limb scan,
                residual limb photography, complete PHI, insurance verification,
                and a suggested Standard Work Order (SWO) based on the preceding
                clinical information — so you can walk into that first
                appointment fully prepared.
              </p>
            </div>

            {/* ---- PATIENT CLINICAL PROFILE (Visible) ---- */}
            <div className="rounded-xl border border-background-200 overflow-hidden">
              <div className="bg-background-50/70 px-5 md:px-6 py-4 border-b border-background-100 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-primary-600">
                    {mockPatient.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </span>
                <div>
                  <h3 className="text-xs font-bold text-foreground-800">
                    Patient Clinical Profile
                  </h3>
                  <p className="text-[10px] text-foreground-400">
                    Anonymized — identifying PHI secured below
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold">
                    Amputation
                  </span>
                  <p className="text-sm font-bold text-foreground-800">
                    {mockPatient.amputationLevel}
                  </p>
                  <p className="text-[10px] text-foreground-500">
                    {mockPatient.amputationDate}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold">
                    K-Level
                  </span>
                  <p className="text-sm font-bold text-foreground-800">
                    K{mockPatient.kLevel} — {mockPatient.kLevelDescription}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold">
                    Insurance
                  </span>
                  <p className="text-sm font-bold text-foreground-800">
                    {mockPatient.insuranceProvider}
                  </p>
                  <p className="text-[10px] text-foreground-500">
                    Verified Active
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold">
                    Mobility Goals
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {mockPatient.mobilityGoals.map((goal) => (
                      <span
                        key={goal}
                        className="inline-flex px-2 py-0.5 rounded-md bg-primary-50 border border-primary-100 text-[10px] font-medium text-primary-700"
                      >
                        {goal}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Insurance + Processed Side-by-Side ---- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ---- Insurance Verification Snapshot ---- */}
              <div className="rounded-xl border border-background-200 bg-white overflow-hidden h-full">
                <div className="bg-background-50/70 px-5 md:px-6 py-4 border-b border-background-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 text-green-700">
                      <i className="ri-shield-check-line text-sm"></i>
                    </span>
                    <div>
                      <h3 className="text-xs font-bold text-foreground-800">
                        Insurance Verification Snapshot
                      </h3>
                      <p className="text-[10px] text-foreground-400">
                        Pre-pulled coverage verification with payer-specific
                        benefits
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-[9px] font-semibold text-green-700 whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    Verified Active
                  </span>
                </div>
                <div className="p-5 md:p-6 space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="space-y-1">
                      <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Carrier
                      </span>
                      <p className="text-[11px] font-bold text-foreground-800">
                        {financialROI.insuranceCoverage.provider}
                      </p>
                      <p className="text-[9px] text-foreground-500">
                        {financialROI.insuranceCoverage.planType}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Status
                      </span>
                      <p className="text-[11px] font-bold text-green-700">
                        {financialROI.insuranceCoverage.policyStatus}
                      </p>
                      <p className="text-[9px] text-foreground-500">
                        Verified{" "}
                        {financialROI.insuranceCoverage.verificationDate}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Pre-Auth
                      </span>
                      <p className="text-[11px] font-bold text-accent-700">
                        Pending
                      </p>
                      <p className="text-[9px] text-foreground-500">
                        Awaiting evaluation
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        Coverage
                      </span>
                      <p className="text-[11px] font-bold text-foreground-800">
                        {financialROI.insuranceCoverage.coveragePercent}% Plan
                        Pays
                      </p>
                      <p className="text-[9px] text-foreground-500">
                        Deductible: $
                        {financialROI.insuranceCoverage.deductibleRemaining.toLocaleString()}{" "}
                        remaining
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block">
                        OOP Max
                      </span>
                      <p className="text-[11px] font-bold text-foreground-800">
                        $
                        {financialROI.insuranceCoverage.outOfPocketMax.toLocaleString()}
                      </p>
                      <p className="text-[9px] text-foreground-500">
                        $
                        {financialROI.insuranceCoverage.outOfPocketRemaining.toLocaleString()}{" "}
                        remaining
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-background-100 pt-3">
                    <span className="text-[8px] uppercase tracking-wider text-foreground-400 font-semibold block mb-2">
                      Covered Services
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {financialROI.insuranceCoverage.coveredServices.map(
                        (svc) => (
                          <span
                            key={svc}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 border border-green-100 text-[9px] font-medium text-green-700 whitespace-nowrap"
                          >
                            <span className="w-2.5 h-2.5 flex items-center justify-center">
                              <i className="ri-check-line text-[7px]"></i>
                            </span>
                            {svc}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- PROCESSED ITEMS CHECKLIST ---- */}
              <div className="rounded-xl border border-background-200 bg-background-50/70 p-5 md:p-6 h-full">
                <h3 className="text-xs font-bold text-foreground-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-green-100 text-green-600">
                    <i className="ri-check-double-line text-[10px]"></i>
                  </span>
                  What&apos;s Been Processed
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {processedItems.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-3 rounded-lg bg-white border border-background-100"
                    >
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[10px] text-green-600"></i>
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-foreground-800">
                          {item.label}
                        </p>
                        <p
                          className={`text-[10px] text-foreground-500 leading-relaxed mt-0.5 ${item.blurred ? "blur-[5px] select-none" : ""}`}
                        >
                          {item.detail}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ---- Secure Content Transition ---- */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-background-200"></div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50 border border-background-200">
                <span className="w-4 h-4 flex items-center justify-center rounded-full bg-foreground-200 text-foreground-500">
                  <i className="ri-lock-2-fill text-[8px]"></i>
                </span>
                <span className="text-[9px] font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
                  Secured content below — BAA required
                </span>
              </div>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>

            {/* ---- PHI & CONTACT — LOCKED SECTION ---- */}
            <div className="rounded-xl border-2 border-foreground-200 bg-background-50 overflow-hidden">
              <div className="bg-foreground-50 px-5 md:px-6 py-4 border-b border-foreground-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-200 text-foreground-600">
                    <i className="ri-lock-2-fill text-sm"></i>
                  </span>
                  <div>
                    <h3 className="text-xs font-bold text-foreground-800">
                      Protected Health Information &amp; Contact Details
                    </h3>
                    <p className="text-[10px] text-foreground-500">
                      Secured — requires data lease authorization to unlock
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground-200 text-[9px] font-semibold text-foreground-600 uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground-500"></span>
                  Locked
                </span>
              </div>
              <div className="p-5 md:p-6">
                {/* Blurred PHI grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 blur-[6px] select-none pointer-events-none opacity-60">
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Patient Name
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.fullName}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Date of Birth
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {new Date(mockPatient.dateOfBirth).toLocaleDateString(
                        "en-US",
                        { month: "long", day: "numeric", year: "numeric" },
                      )}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Home ZIP Code
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.zipCode}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Insurance Policy ID
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.policyId}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Phone Number
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.phone}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Email Address
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.email}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Preferred Contact
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      {mockPatient.contactMethod}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      Full Address
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      [Patient Home Address]
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-foreground-400 font-semibold block">
                      3D Scan &amp; Photos
                    </span>
                    <p className="text-xs font-bold text-foreground-800">
                      MyFit Mesh + Residual Limb Images
                    </p>
                  </div>
                </div>

                {/* Lock explanation overlay */}
                <div className="mt-4 flex items-start gap-3 p-4 rounded-lg bg-foreground-50 border border-foreground-200">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-foreground-500">
                    <i className="ri-shield-check-line text-sm"></i>
                  </span>
                  <p className="text-[11px] text-foreground-600 leading-relaxed">
                    PHI and contact information remain encrypted until your
                    clinic authorizes the data lease. This protects patient
                    privacy and ensures HIPAA compliance. Upon authorization,
                    all fields unlock instantly with full audit trail logging.
                  </p>
                </div>
              </div>
            </div>

            {/* ---- FIRST-TIME CLINIC SETUP NOTICE ---- */}
            <div className="rounded-xl border-2 border-dashed border-primary-300 bg-primary-50/50 p-5 md:p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-building-4-line text-base"></i>
                </span>
                <div className="space-y-3 flex-1">
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900">
                      First-Time Partnership Setup Required
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed mt-1">
                      Since this is your clinic&apos;s first engagement with
                      Quick Prosthetics, two administrative steps must be
                      completed before patient files can be unlocked:
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <span className="text-[10px] font-bold text-primary-600">
                          1
                        </span>
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-foreground-800">
                          Create Your Clinic Account
                        </p>
                        <p className="text-[10px] text-foreground-500">
                          Secure enterprise profile with your clinic credentials
                          and staff roster
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <span className="text-[10px] font-bold text-primary-600">
                          2
                        </span>
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-foreground-800">
                          Digitally Sign the Business Associate Agreement (BAA)
                        </p>
                        <p className="text-[10px] text-foreground-500">
                          Standardized partnership terms, data handling
                          protocols, and HIPAA BA agreement
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-foreground-600 leading-relaxed">
                    Once your account is active and the BAA is signed, our
                    system will provide you with full access to all clinical
                    artifacts, the prescribing physician&apos;s contact for dual
                    sign-off, and a cohesive note audit trail to ensure seamless
                    insurance acceptance.
                  </p>
                </div>
              </div>
            </div>

            {/* ---- PHYSICIAN CONNECTION CALL-OUT ---- */}
            <div className="rounded-xl border border-accent-200 bg-accent-50/60 p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-user-heart-line text-base"></i>
                  </span>
                  <div>
                    <h4 className="text-xs font-bold text-foreground-800">
                      Prescribing Physician Connection
                    </h4>
                    <p className="text-[11px] text-foreground-500 leading-relaxed mt-0.5">
                      Upon account activation, you will be connected directly
                      with Dr. A. Paliga, MD — whose complete clinical notes,
                      medical evaluation, and K-Level assessment are already
                      included in this patient file — for dual sign-off on all
                      clinical artifacts and a cohesive note audit. This
                      collaborative prosthetist-physician communication is vital
                      to maximizing insurance acceptance on first submission.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- CLOSING ---- */}
            <div className="space-y-3 pt-2">
              <p className="text-sm text-foreground-700 leading-relaxed">
                This patient is ready and waiting. All clinical information
                above is visible for your review — the complete package
                including the Standard Work Order, L-Code schedule, and full
                reimbursement breakdown becomes available once your BAA is
                signed. Only the identifying PHI and direct contact channels
                remain secured pending your authorization. The patient has
                selected your clinic specifically to handle their prosthetic
                needs. However, if you decide to not accept the Quick
                Prosthetics data lease fee and BAA within 24 hours, we will
                notify the patient that your clinic has not completed the
                requirements and another clinic will need to be selected.
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                We look forward to supporting your clinic in delivering
                exceptional prosthetic care.
              </p>
              <div className="pt-2">
                <p className="text-xs font-semibold text-foreground-800">
                  Warm regards,
                </p>
                <p className="text-xs text-foreground-600">
                  The Quick Prosthetics Clinical Network Team
                </p>
              </div>
            </div>

            {/* ---- DIVIDER ---- */}
            <div className="border-t border-background-100 pt-6">
              {!proceeding ? (
                <button
                  type="button"
                  onClick={handleProceed}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-xl bg-foreground-900 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-4-line text-base"></i>
                  </span>
                  Create Clinical Account &amp; Unlock Patient Files
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base"></i>
                  </span>
                </button>
              ) : (
                <div className="inline-flex items-center gap-3 px-10 py-4 rounded-xl bg-accent-50 border border-accent-200 animate-fadeSlideIn">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                    <i className="ri-loader-4-line text-base animate-spin"></i>
                  </span>
                  <span className="text-sm font-semibold text-accent-700">
                    Redirecting to Account Setup...
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compliance Footer */}
        <div className="relative z-10 flex items-start gap-2.5 px-4 py-4">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
            <i className="ri-information-line text-[11px]"></i>
          </span>
          <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
            {globalComplianceText}
          </p>
        </div>
      </main>
    </div>
  );
}
